import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import {
  AuthenticationResult,
  InteractionRequiredAuthError,
  IPublicClientApplication,
  AccountInfo,
} from "@azure/msal-browser";
import { Method } from "axios";
import httpService from "../services/httpService";
import { api_s2 } from "../config";

export type ApiMethod = Method;

export const callPymbleApi = async (
  instance: IPublicClientApplication,
  method: Method,
  url: string,
  options: any = null,
) => {
  let result: any = null;

  // MSAL v4 requires initialize() before any API call.
  // Idempotent — safe to call even if MsalProvider already initialized.
  await instance.initialize();

  let account: AccountInfo | null = null;

  try {
    const accounts: AccountInfo[] = instance.getAllAccounts();
    account = accounts[0] || null;
  } catch (error: any) {
    console.error(
      "callPymbleApi - Error getAllAccounts",
      JSON.stringify(error.message)
    );
    return Promise.reject(error);
  }

  if (account) {
    let response: AuthenticationResult | void;
    try {
      response = await instance.acquireTokenSilent({
        scopes: api_s2.scopes,
        account: account,
      });
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // User needs to re-authenticate — redirect to Azure AD.
        await instance.acquireTokenRedirect({
          scopes: api_s2.scopes,
          account: account,
        });
        return null;
      }
      // Other errors (e.g., network, not-yet-initialized) — let React Query retry.
      console.error("acquireTokenSilent failed:", (error as Error).message);
      return null;
    }

    if (response && response.accessToken) {
      let res = await httpService.request({
        method: method,
        url: url,
        ...options,
        headers: {
          Authorization: `Bearer ${response.accessToken}`,
          ...options?.headers,
        },
      });

      if (options?.responseType === "blob") {
        result = (await res?.data) ?? null;
      } else {
        result = res?.data ? res.data : null;
      }
    }
    else {
      const msg = "usePymbleApi - No access token found.";
      console.error(msg);
      return Promise.reject(msg);
    }
  } else {
    // No account yet — expected on login page before authentication.
    // Return null instead of rejecting to avoid React Query retry spam.
    return null;
  }

  return result ?? null;
};

/**
 * Hook to fetch api data from S2 API (pymble-apis).
 * Params:
 *   method: <string>  GET | POST | PUT | DELETE
 *   url: <string>  Url of endpoint
 *   options: Extra options to include in Axios call, eg { data: {...} }
 *            for a POST request
 */
export function usePymbleApi() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const callApiS2 = async (
    method: Method,
    url: string,
    options: any = null
  ) => {
    let parsedOptions: any | null = options ? { ...options } : null;

    parsedOptions = {
      baseURL: api_s2.baseURL,
      ...parsedOptions,
      headers: {
        Accept: "application/json",
        PymbleClientId: "covers-manager",
        ...parsedOptions?.headers,
      },
    };

    // Make the API request
    let result: any = await callPymbleApi(
      instance,
      method,
      url,
      parsedOptions,
    );
    return result;
  };

  return {
    callApiS2,
    isAuthenticated,
  };
}
